import React, { useContext, useState } from 'react'; // Import useState
import { Link, useNavigate } from 'react-router-dom'; // Import useNavigate
import { AuthContext } from '../context/AuthContext';
import { loadStripe } from '@stripe/stripe-js'; // Import loadStripe

// Load Stripe outside component (can be shared if already loaded in main.jsx, but safe to load here too)
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

function Pricing() {
  const { isAuthenticated, token } = useContext(AuthContext); // Get token for backend calls
  const navigate = useNavigate(); // Hook for navigation
  const [loadingPlan, setLoadingPlan] = useState(null); // State to track loading button, e.g., 'pro', 'premium'
  const [error, setError] = useState(''); // State for errors

  // --- Backend Interaction Required ---
  // This function calls your backend to create a Stripe Checkout Session.
  // Your backend needs an endpoint (e.g., POST /api/payments/create-checkout-session)
  // that uses your Stripe SECRET key.
  const handleCheckout = async (priceId, planIdentifier) => {
    setError(''); // Clear previous errors
    setLoadingPlan(planIdentifier); // Indicate loading for this button

    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      navigate('/login?message=Please log in to purchase a plan.');
      setLoadingPlan(null);
      return;
    }

    try {
      // 1. Call your backend to create the checkout session
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/payments/create-checkout-session`, { // ** YOU NEED TO CREATE THIS BACKEND ENDPOINT **
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`, // Send auth token
        },
        body: JSON.stringify({ priceId: priceId }), // Send the Stripe Price ID for the selected plan
      });

      const session = await response.json();

      if (!response.ok) {
        throw new Error(session.error || 'Failed to create checkout session.');
      }

      // 2. Redirect to the Stripe Checkout URL provided by the backend
      if (session.url) {
        window.location.href = session.url; // Redirect the browser to Stripe's page
      } else {
        // Handle case where backend didn't return a URL (e.g., if backend sends ID instead)
        // This part can be kept if you might switch backend logic later
        const stripe = await stripePromise;
        const { error: stripeError } = await stripe.redirectToCheckout({
          sessionId: session.id, // Assumes backend sends { id: ... } if no URL
        });
        if (stripeError) {
          console.error("Stripe redirectToCheckout error:", stripeError);
          setError(`Payment Error: ${stripeError.message}`);
        }
      }

    } catch (err) {
      console.error("Checkout process error:", err);
      setError(err.message || 'An error occurred during the checkout process.');
    } finally {
      setLoadingPlan(null); // Stop loading indicator regardless of outcome
    }
  };
  // --- End Backend Interaction ---


  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Display Error Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 border border-red-400 rounded">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Individual Plans Title */}
        <h1 className="text-4xl font-heading text-center mb-4">
          A Plan for Every Artist
        </h1>
        <p className="text-xl text-center font-body mb-12">
          Choose your creative journey.
        </p>

        {/* Individual Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Free Tier */}
          <div className="bg-white p-8 rounded-lg border border-gray-200 flex flex-col justify-between">
            <div>
              <h2 className="text-2xl font-body mb-4">Explorer</h2>
              <p className="text-5xl font-heading mb-6">Free<span className="text-lg font-normal text-gray-500"></span></p>
              <ul className="space-y-3 font-body text-gray-700 mb-8">
                <li>✓ Introduction Workshops</li>
                <li>✓ Basic AI Feedback</li>
                <li className="text-gray-400">✗ Full Module Library</li>
                <li className="text-gray-400">✗ Advanced AI Agent</li>
              </ul>
            </div>
            {isAuthenticated ? (
              <button
                disabled
                className="w-full bg-gray-300 text-gray-500 px-6 py-3 rounded-md font-body cursor-not-allowed mt-4"
              >
                Current Plan
              </button>
            ) : (
              <Link
                to="/register"
                className="w-full block text-center bg-black text-white px-6 py-3 rounded-md font-body hover:bg-gray-800 transition mt-4"
              >
                Sign Up
              </Link>
            )}
          </div>

          {/* Paid Tier (Creator Pro - Beta Pricing) */}
          <div className="bg-customGreen text-white p-8 rounded-lg relative overflow-hidden flex flex-col justify-between">
            <div>
              <h2 className="text-2xl font-body mb-4">Creator Pro <span className="text-sm font-semibold bg-yellow-300 text-yellow-900 px-2 py-0.5 rounded ml-2">Beta</span></h2>
              {/* Pricing with Discount */}
              <div className="flex items-baseline mb-6">
                <p className="text-5xl font-heading mr-2">$25</p> {/* Assuming $100 is the correct beta price */}
                <span className="text-lg font-heading opacity-80">/ month</span>
              </div>
              <ul className="space-y-3 mb-8 font-body">
                <li>✓ Everything in Explorer</li>
                <li>✓ Full Module Library</li>
                <li>✓ Advanced AI Agent</li>
                <li>✓ Drop-in Office Hours</li>
              </ul>
            </div>
            {/* Changed Button to Anchor Link for direct Stripe Checkout */}
            <Link
              to="https://forms.gle/Ubr6LxuaDg42hrE27" // Link to the new info page
              className="w-full block text-center bg-white text-black px-6 py-3 rounded-md font-body hover:bg-gray-100 transition mt-4"
              target="_blank" // Open in new tab
            >
              <span>Join Waitlist</span>
            </Link>
          </div>

          {/* Premium Plan */}
          <div className="bg-black text-white p-8 rounded-lg border border-gray-200 flex flex-col justify-between">
            <div>
              <h3 className="text-2xl font-body mb-4">Creator Academy</h3>
              {/* Pricing with Discount */}
              <div className="flex items-baseline mb-2">
                <p className="text-5xl font-heading mr-2">$299</p>
                <p className='text-lg font-heading opacity-80'><span className="line-through">$499</span> / one-time</p>
              </div>
              <p className="text-sm text-gray-400 mb-6 font-medium">Early-bird pricing (first 50 seats)</p>
              <ul className="space-y-3 font-body text-white mb-8">
                <li>✓ Classes Taught by MIT Lecturer</li>
                <li>✓ Weekly Small-groups to Review Topics </li>
                <li>✓ Digital Assignments via Makerstate Platform</li>
                <li>✓ Everything in Creator Pro for Duration</li>
              </ul>
            </div>
            <Link
              to="https://drive.google.com/file/d/1rvKsV9By4od8qfjaCNGMEabqpQDJCZfS/view?usp=sharing" // Keep Sponsor link as is, or adjust if needed
              className="w-full block text-center border border-white bg-black text-white px-6 py-3 rounded-md font-body transition mt-4 hover:bg-white hover:text-black" // Added hover effect
              target="_blank" // Open in new tab
            >
              Sponsor an Artist
            </Link>
            {/* Changed Link to Anchor Tag for external Google Form */}
            <a
              href="/creator-academy" // Link to Google Form
              target="_blank" // Open in new tab
              rel="noopener noreferrer" // Security for target="_blank"
              className="w-full block text-center bg-white text-black px-6 py-3 rounded-md font-body hover:bg-gray-100 transition mt-4" // Active button styles
            >
              Enroll Now
            </a>
          </div>
        </div>

        {/* Separator */}
        <hr id="partners" className="my-16 border-gray-300" />

        {/* Organizational Plans Section */}
        <div className="text-center">
          <h2 className="text-4xl font-heading mb-4">
            A Plan for Every Organization
          </h2>
          <p className="text-xl font-body mb-12 max-w-2xl mx-auto">
            Empower artists and measure impact. We partner with universities, nonprofits, and foundations to provide bulk licenses and tailored support.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Organization Plan 1 */}
          <div className="bg-white p-8 rounded-lg border border-gray-200 flex flex-col justify-between">
            <div>
              <h3 className="text-2xl font-body mb-4">Creator Max</h3>
              <p className="text-gray-600 font-body mb-6">Ideal for cohorts, classrooms, or departments.</p>
              <ul className="space-y-3 font-body text-gray-700 mb-8">
                <li>✓ Bulk Creator Pro licenses</li>
                <li>✓ Dedicated onboarding support</li>
                <li>✓ Basic usage reporting</li>
                <li>✓ Discounted volume pricing</li>
              </ul>
            </div>
            <Link
              to="/contact" // Link to your contact page
              className="w-full block text-center bg-black text-white px-6 py-3 rounded-md font-body transition mt-4"
            >
              Contact Sales
            </Link>
          </div>

          {/* Organization Plan 2 */}
          <div className="bg-white p-8 rounded-lg border border-gray-200 flex flex-col justify-between">
            <div>
              <h3 className="text-2xl font-body mb-4">Impact Partner</h3>
              <p className="text-gray-600 font-body mb-6">Designed to help foundations/nonprofits measure impact.</p>
              <ul className="space-y-3 font-body text-gray-700 mb-8">
                <li>✓ Everything in Team License</li>
                <li>✓ Advanced impact measurement tools</li>
                <li>✓ Customizable reporting dashboards</li>
                <li>✓ Co-branded platform options</li>
              </ul>
            </div>
            <Link
              to="/contact" // Link to your contact page
              className="w-full block text-center bg-black text-white px-6 py-3 rounded-md font-body transition mt-4"
            >
              Request a Demo
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Pricing;